import type { DesignTokens } from "../types/theme";

/**
 * ── Folio · Typographic Identity ───────────────────────────────────
 * Premium editorial: a prominent display serif (Newsreader) for
 * titles/brand and a clean grotesque (Hanken Grotesk) for body text.
 * Strings include their fallbacks so text remains readable if the font does
 * not load (offline, CDN hiccup).
 */
const FOLIO_SANS =
  "'Hanken Grotesk', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const FOLIO_SERIF = "'Newsreader', 'Source Serif 4', Georgia, 'Times New Roman', serif";

/**
 * Base tokens for Light mode — Folio "Pine ink on paper": cold ivory
 * paper + pine ink, pine/laurel accent, gold highlight.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f1f4ec",
  foreground: "#1f2d27",
  surface: "#f7faf4",
  surfaceForeground: "#1f2d27",
  surfaceSecondary: "#e7ece2",
  surfaceSecondaryForeground: "#1f2d27",
  surfaceTertiary: "#dde4d7",
  surfaceTertiaryForeground: "#1f2d27",
  overlay: "#f7faf4",
  overlayForeground: "#1f2d27",
  muted: "#5c6c65",
  default: "#e2e8dc",
  defaultForeground: "#1f2d27",
  accent: "#2f5a47",
  accentForeground: "#f7faf4",
  danger: "#b23b2c",
  dangerForeground: "#f7faf4",
  warning: "#ac7f2e",
  warningForeground: "#1f2d27",
  success: "#3d8a5b",
  successForeground: "#f7faf4",
  separator: "#cdd6c9",
  glassBackground: "rgba(247, 250, 244, 0.82)",
  glassBorder: "rgba(31, 45, 39, 0.1)",
  glassHighlight: "rgba(31, 45, 39, 0.06)",
  glow0: "rgba(47, 90, 71, 0.1)",
  glow1: "rgba(172, 127, 46, 0.08)",
  glow2: "rgba(61, 138, 91, 0.06)",
  mutedForeground: "#6a776f",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(31, 45, 39, 0.08)",
  overlayShadow: "rgba(31, 45, 39, 0.1)",
  fieldShadow: "rgba(31, 45, 39, 0.06)",
  fieldBorder: "rgba(31, 45, 39, 0.1)",
  fieldPlaceholder: "rgba(31, 45, 39, 0.4)",
  fieldBackground: "rgba(31, 45, 39, 0.03)",
  segment: "#f7faf4",
  segmentForeground: "#1f2d27",
  link: "#2f5a47",
  focus: "#2f5a47",
  fieldForeground: "#1f2d27",
  radius: "0.5rem",
  fontNormal: FOLIO_SANS,
  fontMedium: FOLIO_SANS,
  fontSemiBold: FOLIO_SERIF,
  fontBold: FOLIO_SERIF,
  border: "#d5ddcf",
  primary: "#2f5a47",
  primaryForeground: "#f7faf4",
  secondary: "#e2e8dc",
  secondaryForeground: "#1f2d27",
  backdrop: "rgba(31, 45, 39, 0.4)",
};

/**
 * Base tokens for Dark mode — Folio: deep pine ink (not pure black) +
 * luminous laurel accent, gold highlight.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#18211d",
  foreground: "#f1f5ee",
  surface: "#1e2a25",
  surfaceForeground: "#f1f5ee",
  surfaceSecondary: "#26332d",
  surfaceSecondaryForeground: "#f1f5ee",
  surfaceTertiary: "#303e37",
  surfaceTertiaryForeground: "#f1f5ee",
  overlay: "#26332d",
  overlayForeground: "#f1f5ee",
  muted: "#a0b0a6",
  default: "#303e37",
  defaultForeground: "#f1f5ee",
  accent: "#6cb495",
  accentForeground: "#18211d",
  danger: "#df8474",
  dangerForeground: "#18211d",
  warning: "#d8b263",
  warningForeground: "#18211d",
  success: "#6fc38f",
  successForeground: "#18211d",
  separator: "#35443c",
  glassBackground: "rgba(24, 33, 29, 0.88)",
  glassBorder: "rgba(241, 245, 238, 0.12)",
  glassHighlight: "rgba(241, 245, 238, 0.2)",
  glow0: "rgba(108, 180, 149, 0.4)",
  glow1: "rgba(216, 178, 99, 0.3)",
  glow2: "rgba(111, 195, 143, 0.24)",
  mutedForeground: "#b0bfb5",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.3)",
  overlayShadow: "rgba(0, 0, 0, 0.35)",
  fieldShadow: "rgba(0, 0, 0, 0.25)",
  fieldBorder: "rgba(241, 245, 238, 0.1)",
  fieldPlaceholder: "rgba(241, 245, 238, 0.4)",
  fieldBackground: "rgba(241, 245, 238, 0.03)",
  segment: "#35443c",
  segmentForeground: "#f1f5ee",
  link: "#6cb495",
  focus: "#6cb495",
  fieldForeground: "#f1f5ee",
  radius: "0.5rem",
  fontNormal: FOLIO_SANS,
  fontMedium: FOLIO_SANS,
  fontSemiBold: FOLIO_SERIF,
  fontBold: FOLIO_SERIF,
  border: "#35443c",
  primary: "#6cb495",
  primaryForeground: "#18211d",
  secondary: "#303e37",
  secondaryForeground: "#f1f5ee",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
